import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { FormikProps } from 'formik';

import { Attention } from '@travel/icons/ui';
import { useDialogHandler } from '@travel/traveler-core/hooks';
import useDeviceType, { MOBILE_SCREEN } from '@travel/traveler-core/hooks/useDeviceType';
import { FlatButton, Form, TextField } from '@travel/ui';

import PrivacyPolicy from 'components/PrivacyPolicy';

import { pushLocation } from 'store/__router/actions';
import { fetchReservationEntrance } from 'store/reservationEntrance/actions';
import { getErrors } from 'store/reservationEntrance/selectors';
import { fetchReservationList } from 'store/reservationList/actions';

import { Translate } from 'core/translate';
import paths from 'core/universalRouter/paths';
import type { SORT_OPTION_TYPE, STATUS_TYPE } from 'ReservationList-Types';

import styles from '../reservationEntrancePage.module.scss';

type Props = {
  query?: {
    callback?: string;
  };
  checkReservationFormSubmitted?: ((data: boolean) => void) | undefined;
};

export type AuthData = {
  bookingId?: string;
  confirmationKey?: string;
};

function ReservationCheckingForm(props: Props) {
  const { query, checkReservationFormSubmitted } = props;
  const dispatch = useDispatch();
  const error = useSelector(getErrors);
  const [submitted, setSubmitted] = useState(false);
  const isMobile = useDeviceType() === MOBILE_SCREEN;

  const idLabel = <Translate id="Reservation_Entrance.Form.BookingID" />;
  const keyLabel = <Translate id="Reservation_Entrance.Form.Confirmation_Key" />;
  const contactText = <Translate id="Reservation_Entrance.Contact_Disclaimer" />;

  const pageName =
    error?.status && submitted
      ? 'booking:wrong_booking_id_or_confirmation_key_message'
      : 'booking:booking_id_and_confirmation_key_form';

  const {
    onClose: onPrivacyPolicyClose,
    onOpen: onPrivacyPolicyOpen,
    isOpen: isPrivacyPolicyOpen,
  } = useDialogHandler(false);

  const handleSubmit = async (data: AuthData) => {
    const reqParam = {
      bookingNo: data.bookingId || '',
      confirmationKey: data.confirmationKey || '',
    };

    const token = await dispatch(fetchReservationEntrance(reqParam));
    if (token) {
      const init = {
        offset: 0,
        limit: 5,
        reservationList: 'UPCOMING' as STATUS_TYPE,
        sort: 'BOOKING_DATE_DESC' as SORT_OPTION_TYPE,
      };
      dispatch(fetchReservationList(init));
      // redirect
      if (query?.callback) {
        dispatch(pushLocation(query.callback));
      } else {
        dispatch(
          pushLocation({
            pathname: paths.reservationList.path,
          }),
        );
      }
    }

    setSubmitted(true);
    checkReservationFormSubmitted?.(true);
  };

  const onClickPrimary = () => {
    dispatch(pushLocation(paths.legacyReservationForm.path));
  };

  const errorText = (
    <Translate id="Traveler_Input_Error.Reservation_Entrance.Booking_ID_Confirmation_Key.Invalid" />
  );

  const errorMessage = () => {
    return (
      <div
        id="errorMessage"
        data-ratid="errorMessage"
        data-ratevent="appear"
        data-ratparam="all"
        data-rat-page_name={pageName}
        className={styles.error}
      >
        <span className={styles.icon}>
          <Attention size={14} />
        </span>
        <span>{errorText}</span>
      </div>
    );
  };

  return (
    <div data-testid="reservation-checking-form">
      <Form
        initialValues={{
          bookingId: '',
          confirmationKey: '',
        }}
        onSubmit={handleSubmit}
      >
        {(formProps: FormikProps<AuthData>) => {
          const isButtonDisabled =
            !formProps.values.bookingId?.trim() || !formProps.values.confirmationKey?.trim();

          return (
            <form onSubmit={formProps.handleSubmit} className={styles.container}>
              {error?.status && submitted && isMobile ? errorMessage() : null}
              <main className={styles.forms}>
                <TextField
                  className={styles.textfield}
                  labelClassName={styles.label}
                  name={'bookingId'}
                  value={formProps.values.bookingId || ''}
                  label={idLabel}
                  onChange={formProps.handleChange}
                  inputProps={{
                    id: 'bookingId',
                  }}
                />
                <TextField
                  className={styles.textfield}
                  labelClassName={styles.label}
                  name={'confirmationKey'}
                  value={formProps.values.confirmationKey || ''}
                  label={keyLabel}
                  onChange={formProps.handleChange}
                  inputProps={{
                    id: 'confirmationKey',
                  }}
                />
              </main>
              {error?.status && submitted && !isMobile ? errorMessage() : null}
              <div className={styles.buttonContainer}>
                <FlatButton
                  className={styles.button}
                  type={'submit'}
                  isDisabled={isButtonDisabled}
                  data-testid="reservationEntrancePage-submit-button"
                >
                  <p>
                    <Translate id="Reservation_Entrance.View_Reservation" />
                  </p>
                </FlatButton>
              </div>

              <div className={styles.mauiContainer}>
                <Translate
                  id="Traveler_Common.Maui_Link_Description"
                  data={{
                    maui_link: (
                      <button
                        onClick={onPrivacyPolicyOpen}
                        className={styles.link}
                        data-testid="reservationEntrance-mauilink"
                        type="button"
                      >
                        <Translate id="Traveler_Common.Maui_Link" className={styles.btn_text} />
                      </button>
                    ),
                  }}
                />
              </div>
            </form>
          );
        }}
      </Form>
      <div className={styles.bottom}>{contactText}</div>

      {isPrivacyPolicyOpen && (
        <PrivacyPolicy
          isPrivacyPolicyOpen={isPrivacyPolicyOpen}
          onPrivacyPolicyClose={onPrivacyPolicyClose}
          onClickPrimary={onClickPrimary}
        />
      )}
    </div>
  );
}

export default ReservationCheckingForm;
